import React, { useState, useEffect } from 'react';
import {
  List,
  Input,
  Select,
  Button,
  Space,
  Tag,
  Badge,
  Empty,
  Spin,
  message,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { KrybotService } from '../../../services/api/krybotService';
import type { KrybotTemplate } from '../../../types/krybot';
import styles from './TemplateManagement.module.css';

const { Search } = Input;
const { Option } = Select;

interface TemplateListPanelProps {
  selectedTemplateId: string | null;
  onSelectTemplate: (templateId: string | null) => void;
  onCreateNew: () => void;
  onRefresh?: () => void;
}

export const TemplateListPanel: React.FC<TemplateListPanelProps> = ({
  selectedTemplateId,
  onSelectTemplate,
  onCreateNew,
  onRefresh,
}) => {
  const [templates, setTemplates] = useState<KrybotTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  useEffect(() => {
    loadTemplates();
  }, [typeFilter, statusFilter]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await KrybotService.listTemplates();
      setTemplates(data);
      onRefresh?.();
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to load templates';
      message.error(`Failed to load templates: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = templates.filter((template) => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        template.template_key.toLowerCase().includes(searchLower) ||
        template.template_type?.toLowerCase().includes(searchLower) ||
        template.sector?.toLowerCase().includes(searchLower) ||
        template.prompt_text.toLowerCase().includes(searchLower)
      );
    }
    if (typeFilter !== 'all') {
      if (template.template_type !== typeFilter) return false;
    }
    if (statusFilter !== 'all') {
      if (statusFilter === 'active' && !template.is_active) return false;
      if (statusFilter === 'inactive' && template.is_active) return false;
    }
    return true;
  });

  // Get unique template types for filter
  const templateTypes = Array.from(new Set(templates.map(t => t.template_type).filter(Boolean)));

  return (
    <div className="template-list-panel">
      <div className="template-list-panel-header">
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={onCreateNew}
            block
          >
            Create New Template
          </Button>
          <Search
            placeholder="Search templates..."
            allowClear
            onSearch={setSearchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            prefix={<SearchOutlined />}
          />
          <Space style={{ width: '100%' }}>
            <Select
              value={typeFilter}
              onChange={setTypeFilter}
              style={{ flex: 1 }}
              size="small"
            >
              <Option value="all">All Types</Option>
              {templateTypes.map((type) => (
                <Option key={type} value={type}>
                  {type}
                </Option>
              ))}
            </Select>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ flex: 1 }}
              size="small"
            >
              <Option value="all">All</Option>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadTemplates}
            size="small"
            block
          >
            Refresh
          </Button>
        </Space>
      </div>

      <div className="template-list-panel-content">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin />
          </div>
        ) : filteredTemplates.length === 0 ? (
          <Empty
            description={searchTerm ? 'No templates found' : 'No templates'}
            style={{ marginTop: '40px' }}
          />
        ) : (
          <List
            dataSource={filteredTemplates}
            renderItem={(template) => (
              <List.Item
                className={`template-list-item ${selectedTemplateId === template.id ? 'selected' : ''}`}
                onClick={() => onSelectTemplate(template.id)}
                style={{
                  cursor: 'pointer',
                  padding: '12px 16px',
                  borderLeft: selectedTemplateId === template.id ? '3px solid #1890ff' : '3px solid transparent',
                }}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <span>{template.template_key}</span>
                      <Badge
                        status={template.is_active ? 'success' : 'default'}
                        text={template.is_active ? 'Active' : 'Inactive'}
                      />
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {template.prompt_text?.substring(0, 100) || 'No prompt text'}...
                      </div>
                      <Space size="small">
                        {template.template_type && (
                          <Tag>{template.template_type}</Tag>
                        )}
                        {template.sector && (
                          <Tag color="blue">{template.sector}</Tag>
                        )}
                      </Space>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </div>
    </div>
  );
};

